import { Link } from "@tanstack/react-router";
import { CursorClick01, LineChartUp02, TrendUp01 } from "@untitledui/icons";
import { useState } from "react";
import { useMerchant } from "@/providers/MerchantProvider";
import { productExplorerRoute } from "@/router";
import { CreatorCampaignsSection } from "./creator-campaigns-section";

type PerformanceTabProps = {
  creatorId: string;
  creatorName: string;
  data: {
    performanceMetrics?: {
      ctr: number;
      ctor: number;
      orders: number;
      revenue: number;
      gpm: number;
      aov: number;
    } | null;
    dailyGMV?: Array<{ date: string; revenue: number | null }>;
    behavior?: {
      ordersPerWeek: number;
      postsPerWeek: number;
    };
    topProducts?: Array<{
      productIds: string[];
      productNames?: string[];
      totalRevenue: number;
      unitsSold: number;
      lastSaleDate: string;
    }>;
    sellerRating?: number | null;
    averageCommissionRate?: number | null;
    numberOfPromotedProducts?: number | null;
  };
  currencyFormatter: Intl.NumberFormat;
};

const numFormatter = new Intl.NumberFormat("en-US", {
  compactDisplay: "short",
  notation: "compact",
});

export const PerformanceTab = ({ creatorId, creatorName, data, currencyFormatter }: PerformanceTabProps) => {
  const merchant = useMerchant();
  // Use real data or show empty states
  const performanceMetrics = data.performanceMetrics;
  const behavior = data.behavior || { ordersPerWeek: 0, postsPerWeek: 0 };
  const products = data.topProducts || [];
  const dailyGMV = data.dailyGMV || [];

  // State for hover tooltip
  const [hoveredBar, setHoveredBar] = useState<{
    index: number;
    x: number;
    y: number;
  } | null>(null);

  const maxGMV = dailyGMV.length > 0 ? Math.max(...dailyGMV.map((item) => item.revenue || 0)) : 1;

  // Handle mouse move to update tooltip position
  const handleMouseMove = (e: React.MouseEvent<HTMLElement>, index: number) => {
    setHoveredBar({
      index,
      x: e.clientX,
      y: e.clientY,
    });
  };

  // Helper to format relative time for last sale
  const formatRelativeTime = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Yesterday";
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return `${Math.floor(diffDays / 30)} months ago`;
  };

  if (!performanceMetrics) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-center p-8">
          <div className="text-sm text-tertiary">Performance data is not yet available for this creator.</div>
        </div>

        <div className="mt-15">
          <CreatorCampaignsSection creatorId={creatorId} creatorName={creatorName} />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="inline-flex justify-start items-center gap-1.5">
        <TrendUp01 className="w-5 h-5 text-tertiary" strokeWidth={2} />
        <div className="justify-start text-tertiary text-sm font-medium leading-tight">
          Performance over the last 30 days
        </div>
      </div>

      {/* Performance Stats Table */}
      <div className="flex flex-col rounded-md outline-1 outline-offset-[-1px] outline-border-primary overflow-hidden">
        <div className="self-stretch inline-flex justify-between items-center">
          <div className="flex-1 py-3 bg-white inline-flex flex-col justify-center items-center gap-4">
            <div className="flex flex-col justify-start items-center">
              <div className="justify-start text-primary text-base font-medium leading-normal">
                {(performanceMetrics.ctr * 100).toFixed(2)}%
              </div>
              <div className="justify-start text-tertiary text-sm font-normal leading-tight">CTR</div>
            </div>
          </div>
          <div className="w-0 self-stretch border-l border-border-primary" />
          <div className="flex-1 py-3 bg-white inline-flex flex-col justify-center items-center gap-4">
            <div className="flex flex-col justify-start items-center">
              <div className="justify-start text-primary text-base font-medium leading-normal">
                {(performanceMetrics.ctor * 100).toFixed(2)}%
              </div>
              <div className="justify-start text-tertiary text-sm font-normal leading-tight">CTOR</div>
            </div>
          </div>
          <div className="w-0 self-stretch border-l border-border-primary" />
          <div className="flex-1 py-3 bg-white inline-flex flex-col justify-center items-center gap-4">
            <div className="flex flex-col justify-start items-center">
              <div className="justify-start text-primary text-base font-medium leading-normal">
                {numFormatter.format(performanceMetrics.orders)}
              </div>
              <div className="justify-start text-tertiary text-sm font-normal leading-tight">Orders</div>
            </div>
          </div>
        </div>
        <div className="w-full h-0 border-t border-border-primary" />
        <div className="self-stretch inline-flex justify-between items-center">
          <div className="flex-1 py-3 bg-white inline-flex flex-col justify-center items-center gap-4">
            <div className="flex flex-col justify-start items-center">
              <div className="justify-start text-primary text-base font-medium leading-normal">
                {currencyFormatter.format(performanceMetrics.revenue)}
              </div>
              <div className="justify-start text-tertiary text-sm font-normal leading-tight">Revenue</div>
            </div>
          </div>
          <div className="w-0 self-stretch border-l border-border-primary" />
          <div className="flex-1 py-3 bg-white inline-flex flex-col justify-center items-center gap-4">
            <div className="flex flex-col justify-start items-center">
              <div className="justify-start text-primary text-base font-medium leading-normal">
                {currencyFormatter.format(performanceMetrics.gpm)}
              </div>
              <div className="justify-start text-tertiary text-sm font-normal leading-tight">GPM</div>
            </div>
          </div>
          <div className="w-0 self-stretch border-l border-border-primary" />
          <div className="flex-1 py-3 bg-white inline-flex flex-col justify-center items-center gap-4">
            <div className="flex flex-col justify-start items-center">
              <div className="justify-start text-primary text-base font-medium leading-normal">
                {currencyFormatter.format(performanceMetrics.aov)}
              </div>
              <div className="justify-start text-tertiary text-sm font-normal leading-tight">AOV</div>
            </div>
          </div>
        </div>
      </div>

      {/* GMV Breakdown and Behaviour */}
      <div className="flex gap-12 mt-15">
        {/* GMV Breakdown Chart */}
        <div className="w-[62%] space-y-4 relative">
          <div className="flex items-center gap-2">
            <LineChartUp02 className="w-4 h-4 text-tertiary" />
            <h3 className="text-sm font-medium text-secondary">GMV breakdown over the last 30 days</h3>
          </div>
          {dailyGMV.length > 0 ? (
            <div className="relative pl-12">
              {/* Y-axis labels */}
              <div className="absolute left-0 top-0 h-48 flex flex-col justify-between text-xs text-tertiary">
                <span>{currencyFormatter.format(maxGMV)}</span>
                <span>{currencyFormatter.format(maxGMV * 0.66)}</span>
                <span>{currencyFormatter.format(maxGMV * 0.33)}</span>
                <span>{currencyFormatter.format(0)}</span>
              </div>

              {/* Chart bars */}
              <div className="flex items-end justify-between gap-2 h-48">
                {dailyGMV.map((item, index) => {
                  const barHeight = item.revenue ? (item.revenue / maxGMV) * 100 : 0;

                  return (
                    <button
                      type="button"
                      key={item.date}
                      className="flex flex-1 items-end justify-center h-48 relative cursor-pointer border-none bg-transparent p-0"
                      onMouseMove={(e) => handleMouseMove(e, index)}
                      onMouseLeave={() => setHoveredBar(null)}
                    >
                      <div
                        className="w-full bg-[#6F658C] rounded-t hover:bg-[#5d4c75] transition-colors pointer-events-none"
                        style={{
                          height: `${barHeight}%`,
                        }}
                      />
                    </button>
                  );
                })}
              </div>

              {/* Hover tooltip - follows cursor */}
              {hoveredBar !== null && (
                <div
                  className="fixed z-50 bg-white border border-gray-200 rounded-lg shadow-lg p-3 pointer-events-none"
                  style={{
                    left: `${hoveredBar.x}px`,
                    top: `${hoveredBar.y + 20}px`, // Position below cursor
                    transform: "translateX(-50%)",
                  }}
                >
                  <p className="text-sm font-medium text-gray-900 mb-2 whitespace-nowrap">
                    {new Date(dailyGMV[hoveredBar.index].date).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })}
                  </p>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600">GMV:</span>
                    <span className="text-sm font-medium text-gray-900">
                      {dailyGMV[hoveredBar.index].revenue
                        ? currencyFormatter.format(dailyGMV[hoveredBar.index].revenue || 0)
                        : "No Data"}
                    </span>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-center h-64">
              <p className="text-sm text-tertiary italic">No GMV data available for the last 30 days</p>
            </div>
          )}
        </div>

        {/* Behaviour */}
        <div className="w-[33%] space-y-2">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <CursorClick01 className="w-4 h-4 text-tertiary" />
              <h3 className="text-sm font-medium text-secondary">Behaviour</h3>
            </div>
            <div className="h-[1px] bg-border-primary" />
          </div>
          <div className="space-y-2 divide-y divide-border-primary">
            <div className="flex items-center justify-between py-2 pb-4">
              <span className="text-sm text-tertiary">Orders per week (Average)</span>
              <span className="text-sm font-medium text-primary">{behavior.ordersPerWeek.toFixed(1)}</span>
            </div>
            <div className="flex items-center justify-between py-2">
              <span className="text-sm text-tertiary">Posts per week (Average)</span>
              <span className="text-sm font-medium text-primary">{behavior.postsPerWeek.toFixed(1)}</span>
            </div>
          </div>
          <div className="h-[1px] bg-border-primary" />
        </div>
      </div>

      {/* Products Sold Table */}
      <div className="space-y-4 mt-15">
        <div className="border border-border-primary rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-border-primary">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-tertiary">Products sold</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-tertiary">Units sold</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-tertiary">Total GMV</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-tertiary">Last Sale</th>
              </tr>
            </thead>
            <tbody>
              {products.slice(0, 4).map((product, index) => (
                <tr
                  key={`product-${product.productIds.join("-")}-${index}`}
                  className="border-b border-border-primary last:border-0"
                >
                  <td className="px-4 py-3 text-sm text-primary">
                    {product.productIds.length === 1 ? (
                      <Link
                        to={productExplorerRoute.to}
                        params={{ merchantId: merchant }}
                        search={{ product_id: product.productIds[0] }}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-primary underline decoration-dotted hover:text-primary/80"
                      >
                        {product.productNames?.[0] || product.productIds[0]}
                      </Link>
                    ) : (
                      <span className="text-sm text-primary">{`${product.productIds.length} products`}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-primary">{numFormatter.format(product.unitsSold)}</td>
                  <td className="px-4 py-3 text-sm text-success-primary font-medium">
                    {currencyFormatter.format(product.totalRevenue)}
                  </td>
                  <td className="px-4 py-3 text-sm text-tertiary">{formatRelativeTime(product.lastSaleDate)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Ad's Summary & Active Campaigns */}
      <div className="mt-15">
        <CreatorCampaignsSection creatorId={creatorId} creatorName={creatorName} />
      </div>

      {/* Bottom Stats */}
      <div className="grid grid-cols-2 gap-12 mt-15">
        <div className="border border-border-primary rounded-lg p-4 bg-gray-50 flex items-start justify-between">
          <div className="w-[80%]">
            <div className="text-sm text-tertiary mb-1">Seller Rating</div>
            <div className="text-xs text-tertiary">From sellers who worked with this creator</div>
          </div>
          {data.sellerRating ? (
            <div className="w-[30%] flex items-start gap-2 justify-end">
              <span className="text-sm">⭐</span>
              <span className="text-sm font-semibold text-primary">{data.sellerRating.toFixed(1)}</span>
              <span className="text-sm text-tertiary">/ 5.0</span>
            </div>
          ) : (
            <div className="w-[30%] flex items-start justify-end">
              <span className="text-sm text-tertiary italic">N/A</span>
            </div>
          )}
        </div>
        <div className="space-y-0 divide-y divide-border-primary border-y border-border-primary">
          <div className="flex items-center justify-between py-3">
            <span className="text-sm text-tertiary">Average Commission rate</span>
            {data.averageCommissionRate ? (
              <span className="text-sm font-medium text-primary">{data.averageCommissionRate.toFixed(1)}%</span>
            ) : (
              <span className="text-sm text-tertiary italic">N/A</span>
            )}
          </div>
          <div className="flex items-center justify-between py-3">
            <span className="text-sm text-tertiary">Number of promoted products</span>
            {data.numberOfPromotedProducts ? (
              <span className="text-sm font-medium text-primary">
                {numFormatter.format(data.numberOfPromotedProducts)}
              </span>
            ) : (
              <span className="text-sm text-tertiary italic">N/A</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
